import React from 'react'
import { useFormsStyles } from '../forms.css'
import { Form, FormSpy } from 'react-final-form'
import { SubmitButtonProps } from 'components/common/submitButton'
import { Trans, useTranslation } from 'react-i18next'
import EmailField from '../components/emailField'
import { validateAWSEmail } from 'appsync-scalar-utils'
import PhoneField from '../components/phoneField'
import PasswordField from '../components/passwordField'
import { FormControl, Grid } from '@material-ui/core'
import TextField from '../components/textField'
import clsx from 'clsx'
import parsePhoneNumberFromString from 'libphonenumber-js'
import PasswordRules from '../components/passwordRules'
import CountryField from '../components/countryField'
import { CountryCode } from 'libphonenumber-js/types'

interface Props {
    subscription?: { [property: string]: boolean }
    onSubmit: (values: SignUpFormState) => void
    children: (content: React.ReactNode, actions: Array<SubmitButtonProps>) => React.ReactNode
    formClassName?: string
    inProgress: boolean
    initialValues: SignUpFormState
}

export interface SignUpFormState {
    email: string
    firstName: string
    lastName: string
    country: CountryCode
    phoneNumber: string
    password: string
    passwordConfirmation: string
}

interface SignUpFormError {
    email?: string
    firstName?: string
    lastName?: string
    country?: string
    phoneNumber?: string
    password?: string
    passwordConfirmation?: string
}

const SignUpForm: React.FC<Props> = ({
    subscription = {
        submitting: true,
        pristine: true,
    },
    onSubmit,
    children,
    formClassName,
    inProgress,
    initialValues,
}) => {
    const formsClasses = useFormsStyles()
    const { t } = useTranslation()

    const strongPasswordRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*\\W)(?=.{8,})')
    const validate = (values: SignUpFormState) => {
        const errors: SignUpFormError = {}

        if (!values.firstName) {
            errors.firstName = t('form-validation.firstName')
        } else if (values.firstName.length < 2) {
            errors.firstName = t('form-validation.firstNameLength')
        }

        if (!values.lastName) {
            errors.lastName = t('form-validation.lastName')
        } else if (values.lastName.length < 2) {
            errors.lastName = t('form-validation.lastNameLength')
        }

        if (!values.email) {
            errors.email = t('form-validation.email')
        } else if (!validateAWSEmail(values.email)) {
            errors.email = t('form-validation.emailValid')
        }

        if (!values.phoneNumber) {
            errors.phoneNumber = t('form-validation.phoneNumber')
        } else if (values.phoneNumber !== '') {
            const phone = parsePhoneNumberFromString(values.phoneNumber, values.country ?? 'FR')
            if (phone === undefined || !phone.isValid()) {
                errors.phoneNumber = t('form-validation.phone')
            }
        }

        if (!values.password) {
            errors.password = t('form-validation.password')
        } else if (!strongPasswordRegex.test(values.password)) {
            errors.password = t('form-validation.passwordStrength')
        }

        if (!values.passwordConfirmation) {
            errors.passwordConfirmation = t('form-validation.passwordConfirmation')
        } else if (values.password !== values.passwordConfirmation) {
            errors.passwordConfirmation = t('form-validation.passwordConfirmationNotMatch')
        }

        return errors
    }

    const handleSubmit = (values: SignUpFormState) => {
        const phone = parsePhoneNumberFromString(values.phoneNumber, values.country ?? 'FR')
        if (phone === undefined || !phone.isValid()) {
            return
        } else {
            values.phoneNumber = phone.number as string
        }
        return onSubmit(values)
    }

    return (
        <Form
            initialValues={initialValues}
            subscription={subscription}
            // debug={console.log}
            onSubmit={handleSubmit}
            validate={validate}
            keepDirtyOnReinitialize
        >
            {({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit} noValidate className={formClassName}>
                    {children(
                        <>
                            <EmailField name="email" label={t('form.email')} required disabled />
                            <div className={clsx(formsClasses.row, formsClasses.emailRow)}>
                                <FormControl component="fieldset" fullWidth required>
                                    <Grid container>
                                        <Grid
                                            item
                                            sm={6}
                                            xs={12}
                                            classes={{
                                                root: formsClasses.firstName,
                                            }}
                                        >
                                            <TextField
                                                name="firstName"
                                                label={t('form.firstName')}
                                                required
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            sm={6}
                                            xs={12}
                                            classes={{
                                                root: formsClasses.lastName,
                                            }}
                                        >
                                            <TextField name="lastName" label={t('form.lastName')} required />
                                        </Grid>
                                    </Grid>
                                </FormControl>
                            </div>
                            <CountryField name="country" />
                            <PhoneField label={t('form.phoneNumber')} name="phoneNumber" required />
                            <PasswordField
                                label={t('form.password')}
                                name="password"
                                autoComplete="new-password"
                                required
                            />
                            <FormSpy subscription={{ values: true }}>
                                {(props) => <PasswordRules value={props.values.password} />}
                            </FormSpy>
                            <PasswordField
                                name="passwordConfirmation"
                                autoComplete="new-password"
                                label={t('form.passwordConfirmation')}
                                required
                            />
                        </>,
                        [
                            {
                                type: 'submit',
                                loading: submitting || inProgress,
                                className: formsClasses.submit,
                                children: <Trans i18nKey="actions.continue" />,
                            },
                        ],
                    )}
                </form>
            )}
        </Form>
    )
}

export default SignUpForm
